/**
 * Supplier invoices.
 *
 * ═══════════════════════════════════════════════════════════════════
 * TWO CONTROLS RUN HERE, AND BOTH ARE VISIBLE.
 *
 * MATCHING compares the invoice with the order and the goods receipt. A price
 * or quantity difference inside tolerance is accepted; outside it, the invoice
 * is held with the discrepancy named — not a generic "mismatch", but which
 * line, what was ordered, what was received, what was invoiced and by how much
 * they differ.
 *
 * AUTHORISATION is a different person from the one who entered the invoice.
 * The server enforces that, and it is the reason a Finance Officer can key an
 * invoice all day and never release a penny.
 * ═══════════════════════════════════════════════════════════════════
 *
 * Every key read below is one the API actually sends, and every rule this
 * screen applies — which buttons are legal, what counts as a clean match, what
 * a hold needs — is the server's own rule restated. That is deliberate: a
 * screen that guesses at the contract ends up offering actions the server
 * refuses and printing "—" where the figures should be.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTerms } from '../lib/tenant';
import {
  date,
  dateTime,
  money,
  number,
  numClass,
  statusLabel,
  statusTone,
  type BadgeTone,
} from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  DefinitionList,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  TextArea,
} from '../components/ui';

/**
 * The states a purchase invoice can actually be in, worth offering as a filter.
 * These are the ledger's own words (purchase_invoice.status), so the menu can
 * never ask for something the ledger has never heard of and come back empty.
 */
const STATUS_FILTERS = [
  'coding',
  'matching',
  'matched',
  'discrepancy',
  'pending_approval',
  'posted',
  'part_paid',
  'paid',
  'disputed',
];

/**
 * A payment hold is a flag on the invoice, not a status, so the ledger cannot
 * be asked for it. It belongs in this menu because that is where a finance user
 * looks for it, so it is applied to the answer instead of to the query.
 */
const ON_HOLD = 'is_on_hold';

/** An invoice can still be matched right up until it is authorised. */
const MATCHABLE = ['received', 'extracting', 'coding', 'matching', 'matched', 'discrepancy'];

/** ...and authorised from any of those, or once it is sitting with an approver. */
const AUTHORISABLE = [...MATCHABLE, 'pending_approval'];

/**
 * The two outcomes the matching engine counts as clean. Anything else is a
 * difference somebody has to decide about. Held identical to the server's own
 * test so the screen and the engine cannot drift apart.
 */
const CLEAN_OUTCOMES = ['matched', 'within_tolerance'];

/**
 * How the figures reached the system, in words a reader outside finance IT
 * understands. Deliberately not run through statusLabel(): "Ocr local" is a
 * column value, not an explanation of where the numbers came from.
 */
const CAPTURE_SOURCE: Record<string, string> = {
  manual: 'Keyed in by hand',
  ocr_local: 'Read by the built-in document parser',
  ocr_ai: 'Read by an AI-assisted extractor',
  integration: 'Received from a supplier feed',
  migration: 'Brought across from the previous system',
};

const MATCH_TYPE_TITLE: Record<string, string> = {
  three_way: 'Three-way match',
  two_way: 'Two-way match',
  manual: 'Manual match',
  none: 'Matching',
};

/**
 * Tone for a match outcome. statusTone() does not know this vocabulary, and a
 * variance shown in the same neutral grey as a clean line would bury the one
 * row in the table that needs a decision.
 */
function matchTone(outcome: string): BadgeTone {
  if (outcome === 'matched') return 'ok';
  if (outcome === 'within_tolerance') return 'info';
  return 'warn';
}

/** "12 at £749.00", or as much of it as the engine recorded. */
function quantityAtPrice(quantity: string | null, priceMinor: number | null): string {
  if (quantity === null && priceMinor === null) return '—';
  if (priceMinor === null) return number(quantity);
  if (quantity === null) return money(priceMinor);
  return `${number(quantity)} at ${money(priceMinor)}`;
}

/**
 * Where a line's account and cost centre came from. Worth saying: coding that
 * arrived from the order is coding somebody already approved, and coding a
 * machine proposed should never look like coding a person chose.
 */
function codingSourceNote(source: string, orderTerm: string, supplierTerm: string): string {
  switch (source) {
    case 'purchase_order':
      return `from the ${orderTerm}`;
    case 'ai_suggestion':
      return 'from an AI suggestion, confirmed by a person';
    case 'supplier_default':
      return `from the ${supplierTerm}'s default coding`;
    case 'manual':
      return 'keyed by hand';
    case 'migration':
      return 'brought across from the previous system';
    default:
      return statusLabel(source);
  }
}

/* ------------------------------------------------------------------ *
 * Purchase ledger
 * ------------------------------------------------------------------ */

interface InvoiceRow {
  id: string;
  invoice_no: string;
  supplier_reference: string;
  supplier_name: string;
  invoice_date: string;
  due_date: string;
  gross_total_minor: number;
  outstanding_minor: number;
  status: string;
  match_status: string;
  is_on_hold: boolean;
  order_no: string | null;
  order_id: string | null;
  days_overdue: number | null;
  is_non_po: boolean;
}

export function InvoicesPage() {
  const t = useTerms();
  const { canAny } = useSession();
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');

  // The search box queries the whole ledger, so it waits for a pause in the
  // typing rather than firing a request per keystroke and letting a shorter
  // prefix's answer land last.
  useEffect(() => {
    const handle = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(handle);
  }, [search]);

  const statusParam = filter === ON_HOLD ? '' : filter;
  const list = useAsync(
    () =>
      api.get<{ invoices: InvoiceRow[] }>(
        `/payables/invoices?${new URLSearchParams({
          ...(statusParam ? { status: statusParam } : {}),
          ...(term ? { search: term } : {}),
        })}`,
      ),
    [statusParam, term],
  );

  const canOpenOrder = canAny('po.view.all', 'requisition.create');

  return (
    <>
      <PageHeader
        title="Supplier invoices"
        subtitle="Captured, matched against the order and the receipt, authorised by a second person, then posted."
      />

      <Card
        title="Purchase ledger"
        flush
        actions={
          <div className="row" style={{ gap: '0.5rem' }}>
            <label className="row" style={{ gap: '0.4rem' }}>
              <span className="visually-hidden">Search invoices</span>
              <input
                type="search"
                placeholder={`Invoice, reference or ${t.supplier.toLowerCase()}`}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            </label>
            <label className="row" style={{ gap: '0.4rem' }}>
              <span className="smaller muted">Show</span>
              <select value={filter} onChange={(e) => setFilter(e.currentTarget.value)}>
                <option value="">All invoices</option>
                {STATUS_FILTERS.map((s) => (
                  <option key={s} value={s}>
                    {statusLabel(s)}
                  </option>
                ))}
                <option value={ON_HOLD}>On hold</option>
              </select>
            </label>
          </div>
        }
      >
        <AsyncPanel state={list}>
          {(data) => {
            const rows = filter === ON_HOLD ? data.invoices.filter((i) => i.is_on_hold) : data.invoices;
            const narrowed = filter !== '' || term !== '';
            const grossTotal = rows.reduce((sum, i) => sum + i.gross_total_minor, 0);
            const outstandingTotal = rows.reduce((sum, i) => sum + i.outstanding_minor, 0);

            return (
              <DataTable
                caption="Supplier invoices"
                rows={rows}
                rowKey={(i) => i.id}
                pageSize={25}
                emptyMessage={
                  narrowed
                    ? 'No invoices match this filter.'
                    : 'No supplier invoices have been entered yet.'
                }
                columns={[
                  {
                    key: 'no',
                    header: 'Invoice',
                    sortValue: (i) => i.invoice_no,
                    render: (i) => (
                      <>
                        <Link to={`/invoices/${i.id}`}>
                          <span className="mono">{i.invoice_no}</span>
                        </Link>
                        <div className="smaller muted mono">{i.supplier_reference}</div>
                      </>
                    ),
                  },
                  {
                    key: 'supplier',
                    header: t.supplier,
                    sortValue: (i) => i.supplier_name,
                    render: (i) => (
                      <>
                        <div className="truncate">{i.supplier_name}</div>
                        {i.order_no ? (
                          <div className="smaller muted">
                            {canOpenOrder && i.order_id ? (
                              <Link to={`/orders/${i.order_id}`}>
                                <span className="mono">{i.order_no}</span>
                              </Link>
                            ) : (
                              <span className="mono">{i.order_no}</span>
                            )}
                          </div>
                        ) : i.is_non_po ? (
                          <div className="smaller">
                            <Badge tone="warn">No {t.purchaseOrder.toLowerCase()}</Badge>
                          </div>
                        ) : null}
                      </>
                    ),
                  },
                  {
                    key: 'dated',
                    header: 'Dated',
                    sortValue: (i) => i.invoice_date,
                    render: (i) => <span className="smaller">{date(i.invoice_date)}</span>,
                  },
                  {
                    key: 'due',
                    header: 'Due',
                    sortValue: (i) => i.due_date,
                    render: (i) => {
                      const overdue = i.outstanding_minor > 0 && (i.days_overdue ?? 0) > 0;
                      return (
                        <span className={overdue ? 'num--negative smaller' : 'smaller'}>
                          {date(i.due_date)}
                          {overdue ? ` (${i.days_overdue}d overdue)` : ''}
                        </span>
                      );
                    },
                  },
                  {
                    key: 'gross',
                    header: 'Value',
                    numeric: true,
                    sortValue: (i) => i.gross_total_minor,
                    render: (i) => <Money minor={i.gross_total_minor} />,
                  },
                  {
                    key: 'outstanding',
                    header: 'Outstanding',
                    numeric: true,
                    sortValue: (i) => i.outstanding_minor,
                    render: (i) => <Money minor={i.outstanding_minor} blankIfZero />,
                  },
                  {
                    key: 'match',
                    header: 'Match',
                    sortValue: (i) => statusLabel(i.match_status),
                    render: (i) => <Badge tone={statusTone(i.match_status)}>{statusLabel(i.match_status)}</Badge>,
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    sortValue: (i) => statusLabel(i.status),
                    render: (i) => (
                      <>
                        <Badge tone={statusTone(i.status)}>{statusLabel(i.status)}</Badge>
                        {i.is_on_hold ? (
                          <div className="smaller">
                            <Badge tone="error">On hold</Badge>
                          </div>
                        ) : null}
                      </>
                    ),
                  },
                ]}
                footer={
                  <tr className="is-total">
                    {/* Totals the whole filtered list, not just the page on screen. */}
                    <td colSpan={4}>
                      Total of the {number(rows.length)} invoice{rows.length === 1 ? '' : 's'} in this list
                    </td>
                    <td className="num">{money(grossTotal)}</td>
                    <td className="num">{money(outstandingTotal)}</td>
                    <td colSpan={2} />
                  </tr>
                }
              />
            );
          }}
        </AsyncPanel>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Invoice detail
 * ------------------------------------------------------------------ */

interface InvoiceHeader {
  id: string;
  invoice_no: string;
  supplier_reference: string;
  supplier_name: string;
  supplier_code: string;
  invoice_date: string;
  received_date: string;
  due_date: string;
  net_total_minor: number;
  vat_total_minor: number;
  irrecoverable_vat_minor: number;
  gross_total_minor: number;
  paid_minor: number;
  outstanding_minor: number;
  status: string;
  match_status: string;
  match_type: string | null;
  match_checked_at: string | null;
  is_on_hold: boolean;
  hold_reason: string | null;
  held_by_name: string | null;
  held_at: string | null;
  duplicate_risk_score: number | null;
  extraction_source: string | null;
  extraction_confidence: number | null;
  entered_by_name: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  journal_id: string | null;
  journal_no: string | null;
  order_no: string | null;
  purchase_order_id: string | null;
  is_non_po: boolean;
  non_po_reason: string | null;
}

interface InvoiceLine {
  id: string;
  line_no: number;
  description: string;
  quantity: string;
  unit_price_minor: number;
  net_minor: number;
  vat_minor: number;
  gross_minor: number;
  vat_code: string | null;
  account_code: string;
  cost_centre_code: string;
  coding_source: string | null;
}

/**
 * One line of the matching engine's working. Every measurement is optional
 * because a two-way match records no receipt, and a line with no matching
 * order line at all records only the finding.
 */
interface MatchResult {
  id: string;
  invoice_line_id: string | null;
  match_type: string;
  outcome: string;
  ordered_quantity: string | null;
  received_quantity: string | null;
  invoiced_quantity: string | null;
  ordered_price_minor: number | null;
  invoiced_price_minor: number | null;
  variance_minor: number | null;
  tolerance_applied: string | null;
  message: string;
  checked_at: string;
}

interface InvoiceDocument {
  id: string;
  title: string | null;
  filename: string;
  document_type: string;
  byte_size: number;
  uploaded_at: string;
  uploaded_by_name: string | null;
}

interface ApprovalStep {
  id: string;
  stage_no: number;
  stage_name: string;
  status: string;
  assigned_to_name: string | null;
  acted_by_name: string | null;
  on_behalf_of_name: string | null;
  acted_at: string | null;
  due_at: string | null;
  decision_comment: string | null;
}

interface PaymentAllocation {
  id: string;
  allocated_minor: number;
  discount_minor: number;
  payment_reference: string;
  payment_date: string;
  payment_status: string;
  batch_no: string | null;
}

interface InvoiceDetail {
  invoice: InvoiceHeader;
  lines: InvoiceLine[];
  matchResults: MatchResult[];
  documents: InvoiceDocument[];
  approval: { steps: ApprovalStep[] } | null;
  payments: PaymentAllocation[];
}

interface MatchOutcomeResponse {
  matchStatus: string;
  isClean: boolean;
  summary: string;
}

interface AuthoriseResponse {
  journalNo: string;
  commitmentRelievedMinor: number;
  assetsCreated: Array<{ assetNo: string; name: string; costMinor: number }>;
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can, canAny } = useSession();
  const t = useTerms();
  const detail = useAsync(() => api.get<InvoiceDetail>(`/payables/invoices/${id}`), [id]);

  // A payment allocation names its run only by number, so the list of runs is
  // fetched to turn that number into a way through to the run itself.
  const runs = useAsync(
    () => api.get<{ batches: Array<{ id: string; batch_no: string }> }>('/payables/payment-runs'),
    [],
  );

  const [holdDialog, setHoldDialog] = useState<'place' | 'release' | null>(null);
  const [holdReason, setHoldReason] = useState('');
  const [overriding, setOverriding] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [matchOutcome, setMatchOutcome] = useState<MatchOutcomeResponse | null>(null);
  const [posted, setPosted] = useState<AuthoriseResponse | null>(null);

  const match = useMutation(async () => api.post<MatchOutcomeResponse>(`/payables/invoices/${id}/match`, {}));
  const authorise = useMutation(async (body: { allowDiscrepancy?: boolean; discrepancyReason?: string }) =>
    api.post<AuthoriseResponse>(`/payables/invoices/${id}/authorise`, body),
  );
  // The route requires the flag as well as the reason: the same endpoint both
  // places and releases a hold, and each is recorded in the audit trail.
  const hold = useMutation(async (input: { hold: boolean; reason: string }) =>
    api.post(`/payables/invoices/${id}/hold`, input),
  );
  const openDocument = useMutation(async (doc: InvoiceDocument) =>
    api.download(`/operations/documents/${doc.id}/content`, doc.filename),
  );

  const openHoldDialog = (mode: 'place' | 'release') => {
    setHoldReason('');
    setHoldDialog(mode);
  };

  const runAuthorise = (body: { allowDiscrepancy?: boolean; discrepancyReason?: string }) => {
    void authorise.run(body).then((result) => {
      if (result === null) return;
      setPosted(result);
      setOverriding(false);
      detail.reload();
    });
  };

  return (
    <AsyncPanel state={detail}>
      {(data) => {
        const inv = data.invoice;
        // The matching engine records the invoice line by id, not by number, so
        // the line number a reader recognises is recovered from the lines above.
        const lineNoById = new Map<string, number>(data.lines.map((l) => [l.id, l.line_no] as const));
        const runIdByNo = new Map<string, string>(
          (runs.data?.batches ?? []).map((b) => [b.batch_no, b.id] as const),
        );
        const discrepancies = data.matchResults.filter((m) => !CLEAN_OUTCOMES.includes(m.outcome));

        // The server's own test for an invoice it will not post without a
        // written reason. An unmatched invoice — one with no order — is not a
        // discrepancy; it simply takes the approval route instead.
        const unresolvedDiscrepancy = !['matched', 'within_tolerance', 'unmatched'].includes(inv.match_status);

        const details: Array<[ReactNode, ReactNode]> = [
          [t.supplier, `${inv.supplier_code} — ${inv.supplier_name}`],
          ['Invoice date', date(inv.invoice_date)],
          ['Received', date(inv.received_date)],
          ['Due', date(inv.due_date)],
          ['Outstanding', <Money minor={inv.outstanding_minor} />],
        ];
        if (inv.paid_minor > 0) details.push(['Paid to date', <Money minor={inv.paid_minor} />]);
        if (inv.irrecoverable_vat_minor > 0) {
          details.push([
            'Irrecoverable VAT',
            <>
              <Money minor={inv.irrecoverable_vat_minor} />
              <div className="smaller muted">Input VAT that cannot be reclaimed, absorbed into the spend.</div>
            </>,
          ]);
        }
        details.push(['Entered by', inv.entered_by_name ?? '—']);
        details.push([
          'Authorised by',
          inv.approved_by_name ? (
            <>
              {inv.approved_by_name}
              {inv.approved_at ? <div className="smaller muted">{dateTime(inv.approved_at)}</div> : null}
            </>
          ) : (
            'Not yet authorised'
          ),
        ]);
        // 'none' is the engine's way of saying there was nothing to match
        // against, which is a sentence rather than a type of match.
        details.push([
          'Match type',
          inv.match_type && inv.match_type !== 'none'
            ? (MATCH_TYPE_TITLE[inv.match_type] ?? statusLabel(inv.match_type))
            : 'Not applicable',
        ]);

        const capture: Array<[ReactNode, ReactNode]> = inv.extraction_source
          ? [['Source', CAPTURE_SOURCE[inv.extraction_source] ?? statusLabel(inv.extraction_source)]]
          : [];
        // A confidence score only means something where something was read; a
        // hand-keyed invoice has nothing to be confident about.
        if (inv.extraction_source && inv.extraction_source !== 'manual') {
          capture.push([
            'Parser confidence',
            inv.extraction_confidence !== null ? `${inv.extraction_confidence}%` : 'Not scored',
          ]);
        }

        const showMatch = MATCHABLE.includes(inv.status) && !inv.is_non_po && can('ap.invoice.match');
        const showAuthorise =
          AUTHORISABLE.includes(inv.status) && !inv.is_on_hold && inv.journal_id === null && can('ap.invoice.approve');
        const showHold = !inv.is_on_hold && can('ap.invoice.hold');
        const anyAction = showMatch || showAuthorise || showHold;

        return (
          <>
            <PageHeader
              title={`Invoice ${inv.invoice_no}`}
              subtitle={
                <>
                  {inv.supplier_name} · {t.supplier.toLowerCase()} reference{' '}
                  <span className="mono">{inv.supplier_reference}</span>
                </>
              }
              meta={
                <>
                  <Badge tone={statusTone(inv.status)}>{statusLabel(inv.status)}</Badge>
                  <Badge tone={statusTone(inv.match_status)}>{statusLabel(inv.match_status)}</Badge>
                  {inv.is_on_hold ? <Badge tone="error">On hold</Badge> : null}
                  {inv.order_no ? (
                    canAny('po.view.all', 'requisition.create') && inv.purchase_order_id ? (
                      <Link to={`/orders/${inv.purchase_order_id}`}>
                        <span className="mono">{inv.order_no}</span>
                      </Link>
                    ) : (
                      <span className="mono smaller">{inv.order_no}</span>
                    )
                  ) : null}
                  {inv.journal_id && can('gl.view') ? (
                    <Link to={`/ledger/journals/${inv.journal_id}`}>
                      Journal {inv.journal_no ? <span className="mono">{inv.journal_no}</span> : null}
                    </Link>
                  ) : null}
                </>
              }
              actions={
                anyAction ? (
                  <div className="btn-group">
                    {showMatch ? (
                      <button
                        type="button"
                        className="btn"
                        disabled={match.busy}
                        onClick={() =>
                          void match.run().then((result) => {
                            if (result === null) return;
                            setMatchOutcome(result);
                            detail.reload();
                          })
                        }
                      >
                        {match.busy ? 'Matching…' : 'Run the match'}
                      </button>
                    ) : null}
                    {showAuthorise ? (
                      <button
                        type="button"
                        className="btn btn--primary"
                        disabled={authorise.busy}
                        onClick={() => (unresolvedDiscrepancy ? setOverriding(true) : runAuthorise({}))}
                      >
                        {authorise.busy ? 'Authorising…' : 'Authorise and post'}
                      </button>
                    ) : null}
                    {showHold ? (
                      <button type="button" className="btn btn--danger" onClick={() => openHoldDialog('place')}>
                        Place on hold
                      </button>
                    ) : null}
                  </div>
                ) : null
              }
            />

            {/* While the override dialog is open its own notice carries the
                failure, so the same message is not printed twice. */}
            <ErrorNotice error={match.error ?? (overriding ? null : authorise.error) ?? openDocument.error} />

            {posted ? (
              <Alert tone="ok" title="Authorised and posted">
                <p>
                  Posted to the ledger as journal <span className="mono">{posted.journalNo}</span>.
                </p>
                {posted.commitmentRelievedMinor > 0 ? (
                  <p className="smaller">
                    {money(posted.commitmentRelievedMinor)} of commitment against the{' '}
                    {t.purchaseOrder.toLowerCase()} has been released.
                  </p>
                ) : null}
                {posted.assetsCreated.length > 0 ? (
                  <p className="smaller">
                    {number(posted.assetsCreated.length)} capital item
                    {posted.assetsCreated.length === 1 ? '' : 's'} added to the asset register:{' '}
                    {posted.assetsCreated.map((a) => a.assetNo).join(', ')}.
                  </p>
                ) : null}
              </Alert>
            ) : null}

            {matchOutcome ? (
              <Alert tone={matchOutcome.isClean ? 'ok' : 'warn'} title="The match has been run">
                <p>{matchOutcome.summary}</p>
              </Alert>
            ) : null}

            {inv.is_on_hold ? (
              <Alert tone="error" title="This invoice is on hold and cannot be paid">
                <p>{inv.hold_reason ?? 'No reason was recorded.'}</p>
                <p className="smaller muted">
                  Held by {inv.held_by_name ?? 'a colleague'}
                  {inv.held_at ? ` on ${dateTime(inv.held_at)}` : ''}. It is excluded from every payment run until
                  the hold is released.
                </p>
                {can('ap.invoice.hold') ? (
                  <p style={{ marginTop: '0.5rem' }}>
                    <button type="button" className="btn btn--sm" onClick={() => openHoldDialog('release')}>
                      Release the hold
                    </button>
                  </p>
                ) : null}
              </Alert>
            ) : null}

            {(inv.duplicate_risk_score ?? 0) > 0 ? (
              <Alert tone="warn" title={`Duplicate risk score ${inv.duplicate_risk_score}`}>
                <p>
                  This invoice resembles one already on file. The score is calculated from the supplier, the reference,
                  the value and the date — a near-miss reference such as a transposed digit still scores.
                </p>
              </Alert>
            ) : null}

            {discrepancies.length > 0 ? (
              <Alert
                tone="warn"
                title={`The match found ${number(discrepancies.length)} difference${discrepancies.length === 1 ? '' : 's'}`}
              >
                <p className="smaller">
                  Each one is set out line by line below — what was ordered, what was received, what was invoiced and by
                  how much they differ — so somebody can decide rather than guess.
                </p>
                {unresolvedDiscrepancy && inv.journal_id === null ? (
                  <p className="smaller">
                    Until it is resolved, this invoice can only be authorised by someone recording in writing why they
                    are accepting the difference.
                  </p>
                ) : null}
              </Alert>
            ) : null}

            <div className="grid grid--3">
              <div style={{ gridColumn: 'span 2' }} className="stack">
                <Card title="Lines" flush>
                  <DataTable
                    caption={`Lines on invoice ${inv.invoice_no}`}
                    rows={data.lines}
                    rowKey={(l) => l.id}
                    emptyMessage="This invoice has no lines."
                    columns={[
                      { key: 'no', header: '#', width: '3rem', render: (l) => l.line_no },
                      { key: 'desc', header: 'Description', render: (l) => l.description },
                      {
                        key: 'coding',
                        header: 'Coding',
                        render: (l) => (
                          <>
                            <span className="mono smaller">
                              {l.account_code} / {l.cost_centre_code}
                            </span>
                            {l.coding_source ? (
                              <div className="smaller muted">
                                {codingSourceNote(l.coding_source, t.purchaseOrder.toLowerCase(), t.supplier.toLowerCase())}
                              </div>
                            ) : null}
                          </>
                        ),
                      },
                      {
                        key: 'qty',
                        header: 'Qty',
                        numeric: true,
                        // The unit price is the figure a reader checks against
                        // the order, and on a non-PO invoice it appears nowhere
                        // else on the screen.
                        render: (l) => (
                          <>
                            {number(l.quantity)}
                            <div className="smaller muted">at {money(l.unit_price_minor)}</div>
                          </>
                        ),
                      },
                      { key: 'net', header: 'Net', numeric: true, render: (l) => <Money minor={l.net_minor} /> },
                      {
                        key: 'vat',
                        header: 'VAT',
                        numeric: true,
                        render: (l) => (
                          <>
                            <Money minor={l.vat_minor} />
                            {l.vat_code ? <div className="smaller muted mono">{l.vat_code}</div> : null}
                          </>
                        ),
                      },
                      { key: 'gross', header: 'Gross', numeric: true, render: (l) => <Money minor={l.gross_minor} /> },
                    ]}
                    footer={
                      <tr className="is-total">
                        <td colSpan={4}>Invoice total</td>
                        <td className="num">{money(inv.net_total_minor)}</td>
                        <td className="num">{money(inv.vat_total_minor)}</td>
                        <td className="num">{money(inv.gross_total_minor)}</td>
                      </tr>
                    }
                  />
                </Card>

                {data.matchResults.length > 0 ? (
                  <Card
                    title={MATCH_TYPE_TITLE[inv.match_type ?? 'none'] ?? 'Matching'}
                    subtitle={
                      inv.match_type === 'three_way'
                        ? 'The invoice compared line by line with the order and with what was actually received.'
                        : 'The invoice compared line by line with the order.'
                    }
                    flush
                    footer={
                      inv.match_checked_at ? (
                        <span className="smaller muted">Last checked {dateTime(inv.match_checked_at)}.</span>
                      ) : null
                    }
                  >
                    <DataTable
                      caption={`Matching results for invoice ${inv.invoice_no}`}
                      rows={data.matchResults}
                      rowKey={(m) => m.id}
                      emptyMessage="The match has not been run on this invoice yet."
                      columns={[
                        {
                          key: 'line',
                          header: 'Line',
                          width: '3rem',
                          render: (m) => lineNoById.get(m.invoice_line_id ?? '') ?? '—',
                        },
                        {
                          key: 'outcome',
                          header: 'Finding',
                          render: (m) => (
                            <>
                              <Badge tone={matchTone(m.outcome)}>{statusLabel(m.outcome)}</Badge>
                              <div className="smaller muted">{m.message}</div>
                            </>
                          ),
                        },
                        {
                          key: 'ordered',
                          header: 'Ordered',
                          numeric: true,
                          render: (m) => quantityAtPrice(m.ordered_quantity, m.ordered_price_minor),
                        },
                        {
                          key: 'received',
                          header: 'Received',
                          numeric: true,
                          render: (m) => number(m.received_quantity),
                        },
                        {
                          key: 'invoiced',
                          header: 'Invoiced',
                          numeric: true,
                          render: (m) => quantityAtPrice(m.invoiced_quantity, m.invoiced_price_minor),
                        },
                        {
                          key: 'variance',
                          header: 'Variance',
                          numeric: true,
                          render: (m) => (
                            <span className={numClass(m.variance_minor)}>{money(m.variance_minor)}</span>
                          ),
                        },
                        {
                          key: 'tolerance',
                          header: 'Tolerance',
                          render: (m) => <span className="smaller muted">{m.tolerance_applied ?? '—'}</span>,
                        },
                      ]}
                    />
                  </Card>
                ) : null}

                {data.payments.length > 0 ? (
                  <Card title="Payments" flush>
                    <DataTable
                      caption="Payments allocated to this invoice"
                      rows={data.payments}
                      rowKey={(p) => p.id}
                      emptyMessage="Nothing has been paid against this invoice."
                      columns={[
                        {
                          key: 'ref',
                          header: 'Payment',
                          render: (p) => (
                            <>
                              <span className="mono">{p.payment_reference}</span>
                              <div className="smaller">
                                <Badge tone={statusTone(p.payment_status)}>{statusLabel(p.payment_status)}</Badge>
                              </div>
                            </>
                          ),
                        },
                        {
                          key: 'batch',
                          header: 'Run',
                          render: (p) => {
                            if (!p.batch_no) return <span className="smaller muted">—</span>;
                            const runId = runIdByNo.get(p.batch_no);
                            return runId ? (
                              <Link to={`/payments/${runId}`}>
                                <span className="mono smaller">{p.batch_no}</span>
                              </Link>
                            ) : (
                              <span className="mono smaller">{p.batch_no}</span>
                            );
                          },
                        },
                        { key: 'date', header: 'Paid', render: (p) => date(p.payment_date) },
                        {
                          key: 'amount',
                          header: 'Amount',
                          numeric: true,
                          render: (p) => <Money minor={p.allocated_minor} />,
                        },
                      ]}
                      footer={
                        <tr className="is-total">
                          <td colSpan={3}>Paid against this invoice</td>
                          <td className="num">
                            {money(data.payments.reduce((sum, p) => sum + p.allocated_minor, 0))}
                          </td>
                        </tr>
                      }
                    />
                  </Card>
                ) : null}

                {data.documents.length > 0 ? (
                  <Card title="Original documents" subtitle="The scan the figures were taken from.">
                    <ul className="stack stack--sm">
                      {data.documents.map((d) => (
                        <li key={d.id} className="row row--between">
                          <span>
                            <span className="mono smaller">{d.filename}</span>{' '}
                            <span className="muted smaller">
                              {statusLabel(d.document_type)} · {Math.max(1, Math.round(d.byte_size / 1024))} kB ·
                              uploaded {date(d.uploaded_at)}
                            </span>
                          </span>
                          {can('document.view') ? (
                            <button
                              type="button"
                              className="btn btn--sm btn--ghost"
                              disabled={openDocument.busy}
                              onClick={() => void openDocument.run(d)}
                            >
                              Open
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </Card>
                ) : null}
              </div>

              <div className="stack">
                <Card title="Invoice details">
                  <DefinitionList items={details} />
                  {inv.entered_by_name && inv.approved_by_name && inv.entered_by_name !== inv.approved_by_name ? (
                    <p className="smaller muted" style={{ marginTop: '0.75rem' }}>
                      Entered and authorised by different people — the separation the server enforces.
                    </p>
                  ) : null}
                </Card>

                {inv.extraction_source ? (
                  <Card title="How this was captured">
                    <DefinitionList items={capture} />
                    {inv.extraction_source === 'ocr_local' ? (
                      <p className="smaller muted">
                        The parser is built into this server. No external AI or OCR service is called and nothing
                        leaves the network.
                      </p>
                    ) : null}
                    {inv.extraction_source === 'ocr_ai' ? (
                      <p className="smaller muted">
                        AI-assisted extraction. Everything it reads is a proposal for a person to confirm.
                      </p>
                    ) : null}
                    {inv.extraction_source === 'ocr_local' || inv.extraction_source === 'ocr_ai' ? (
                      <p className="smaller muted">
                        Extraction proposes figures for a person to check. Nothing posts to the ledger on a machine's
                        say-so.
                      </p>
                    ) : null}
                  </Card>
                ) : null}

                {inv.is_non_po ? (
                  <Card title={`Raised without a ${t.purchaseOrder.toLowerCase()}`}>
                    <p className="muted">{inv.non_po_reason ?? 'No reason recorded.'}</p>
                    <p className="smaller muted">
                      An invoice with no {t.purchaseOrder.toLowerCase()} cannot be three-way matched, so it needs
                      authorisation on its own merits.
                    </p>
                  </Card>
                ) : null}

                {data.approval && data.approval.steps.length > 0 ? (
                  <Card title="Authorisation">
                    <ul className="timeline">
                      {data.approval.steps.map((s) => (
                        <li
                          key={s.id}
                          className={s.status === 'approved' ? 'timeline__item timeline__item--ok' : 'timeline__item'}
                        >
                          <p className="timeline__title">
                            {s.stage_name} <Badge tone={statusTone(s.status)}>{statusLabel(s.status)}</Badge>
                          </p>
                          {/* Who acted, or — while it is still open — who it is sitting with. */}
                          {s.acted_by_name ? (
                            <p className="timeline__detail">
                              {s.acted_by_name}
                              {s.on_behalf_of_name ? ` on behalf of ${s.on_behalf_of_name}` : ''}
                            </p>
                          ) : s.assigned_to_name ? (
                            <p className="timeline__detail">With {s.assigned_to_name}</p>
                          ) : null}
                          {s.decision_comment ? <p className="timeline__detail">“{s.decision_comment}”</p> : null}
                          {s.acted_at ? (
                            <p className="timeline__time">{dateTime(s.acted_at)}</p>
                          ) : s.due_at ? (
                            <p className="timeline__time">Due {dateTime(s.due_at)}</p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </Card>
                ) : null}
              </div>
            </div>

            <Modal
              open={holdDialog !== null}
              onClose={() => setHoldDialog(null)}
              title={holdDialog === 'release' ? 'Release the hold on this invoice' : 'Place this invoice on hold'}
              footer={
                <>
                  <button type="button" className="btn" onClick={() => setHoldDialog(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={holdDialog === 'release' ? 'btn btn--primary' : 'btn btn--danger'}
                    disabled={hold.busy || holdReason.trim().length < 5}
                    onClick={() =>
                      void hold
                        .run({ hold: holdDialog !== 'release', reason: holdReason.trim() })
                        .then((result) => {
                          if (result === null) return;
                          setHoldDialog(null);
                          detail.reload();
                        })
                    }
                  >
                    {hold.busy
                      ? 'Saving…'
                      : holdDialog === 'release'
                        ? 'Release the hold'
                        : 'Place on hold'}
                  </button>
                </>
              }
            >
              <p>
                {holdDialog === 'release'
                  ? 'Once the hold is released the invoice returns to the payment runs it qualifies for.'
                  : 'An invoice on hold is excluded from every payment run until the hold is released.'}
              </p>
              <TextArea
                label={holdDialog === 'release' ? 'Why is the hold being released?' : 'Why is it being held?'}
                hint="Recorded against the invoice in the audit trail."
                value={holdReason}
                onChange={(e) => setHoldReason(e.currentTarget.value)}
                required
              />
              <ErrorNotice error={hold.error} />
            </Modal>

            <Modal
              open={overriding}
              onClose={() => setOverriding(false)}
              title="Authorise an invoice that has not matched"
              footer={
                <>
                  <button type="button" className="btn" onClick={() => setOverriding(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={authorise.busy || overrideReason.trim().length < 5}
                    onClick={() => runAuthorise({ allowDiscrepancy: true, discrepancyReason: overrideReason.trim() })}
                  >
                    {authorise.busy ? 'Authorising…' : 'Authorise and post'}
                  </button>
                </>
              }
            >
              <p>
                The match on this invoice is unresolved ({statusLabel(inv.match_status).toLowerCase()}). It can still be
                authorised, but the reason is kept with the invoice and is visible to {t.financeTeam.toLowerCase()} and
                to the auditors.
              </p>
              <TextArea
                label="Why is the difference being accepted?"
                hint={`For example: the ${t.supplier.toLowerCase()} has confirmed the revised price in writing.`}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.currentTarget.value)}
                required
              />
              <ErrorNotice error={authorise.error} />
            </Modal>
          </>
        );
      }}
    </AsyncPanel>
  );
}
